// AuthContext — current CustomerUser + sign-in / sign-out actions.
//
// Strategy:
//   - On mount, attempt a silent refresh (the refresh cookie may be
//     valid from a prior session). Success → load /me and become
//     signed-in; failure → stay signed-out.
//   - Login + register update the in-memory access token via the API
//     client; we then read /me to populate the user state.
//   - Logout calls /api/auth/logout (which revokes the refresh cookie)
//     and clears state.
//
// Impersonation (ADR-008):
//   - When the impersonation flow lands on /impersonate or completes
//     /employee/impersonation/start, the SPA holds an impersonation
//     access token in the same in-memory slot as a customer token.
//     The `impersonation` field on the context carries the session
//     metadata. The banner reads from here.
//   - End-impersonation clears both the impersonation context and the
//     in-memory access token. The customer's own session (if they had
//     one in another tab) is unaffected.
//   - An impersonation must survive a page RELOAD. Both the access token and
//     the context live in memory, so a reload arrives with neither, while the
//     server-side session and its cookie are still valid. refresh() below routes
//     through the impersonation cookie path when that is the live session and
//     rehydrates the context from the response, so the rep stays where they were
//     instead of being bounced to /login.
//
// The api client owns access-token storage (in-memory only) and the
// 401 → refresh → retry interceptor. This context is the user-facing
// wrapper around it.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import type { CustomerUserDto } from 'shared';

import * as authApi from '../api/auth.js';
import * as imp from '../api/impersonation.js';
import { refreshSession, setOnAuthLost } from '../api/client.js';

type AuthState =
  | { status: 'loading' }
  | { status: 'signed_out' }
  | {
      status: 'signed_in';
      user: CustomerUserDto;
      impersonation: imp.ImpersonationContextDto | null;
    };

type AuthContextValue = AuthState & {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  // Resolves to the user the refresh landed on, or null when there was no live
  // session. Returned rather than only pushed into state because a caller that
  // awaits it needs the identity in the SAME tick — React has not re-rendered
  // yet, so reading it back off the context would read the pre-refresh value.
  refresh: () => Promise<CustomerUserDto | null>;
  // Path A: consume a Nexus-issued grant token.
  startImpersonationFromGrant: (grant_token: string) => Promise<void>;
  // Path B: after the magic-link confirm + customer picker, this
  // transitions the session to an impersonation.
  startImpersonationAsEmployee: (
    target_customer_user_id: string,
    target_company_id?: string | null,
  ) => Promise<void>;
  endImpersonation: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const queryClient = useQueryClient();

  // Wipe every cached query whenever the authenticated identity changes
  // (login / logout / impersonation start + end). TanStack caches GET
  // responses (e.g. ['quotes']) keyed without a user id, so without this
  // the next session would be served the previous user's cached data —
  // a cross-account leak. Backend responses are JWT-scoped; this just
  // stops a stale render of someone else's rows.
  const resetQueryCache = useCallback(() => {
    queryClient.clear();
  }, [queryClient]);

  const refresh = useCallback(async (): Promise<CustomerUserDto | null> => {
    // ONE call, whichever kind of session this tab holds. refreshSession()
    // picks the cookie path off the tab's persisted session-kind hint and is
    // the SPA's single in-flight refresh — calling the impersonation and
    // customer helpers separately from here is what let a boot-time resume race
    // the 401 interceptor, present the same single-use cookie twice, and burn
    // the family as a replay. A dead impersonation resolves to null and signs
    // this tab out; it deliberately does NOT continue into the customer cookie
    // in the same attempt, which would sign the rep in as an unrelated identity
    // with nothing on screen saying the impersonation had ended. The hint is
    // cleared, so the next load restores a genuine customer session normally.
    const session = await refreshSession();
    if (session) {
      setState({
        status: 'signed_in',
        user: session.user,
        impersonation: session.impersonation ?? null,
      });
      return session.user;
    }
    setState({ status: 'signed_out' });
    return null;
  }, []);

  useEffect(() => {
    // Hook the 401-after-refresh failure path so we transition to
    // signed_out without a page reload. Drop the cache too — the session
    // it belonged to is gone.
    setOnAuthLost(() => {
      resetQueryCache();
      setState({ status: 'signed_out' });
    });
    return () => setOnAuthLost(null);
  }, [resetQueryCache]);

  useEffect(() => {
    // The /impersonate landing route establishes the session itself via the
    // grant exchange (ImpersonateExchange). A concurrent boot-time refresh
    // races it: on a brand-new account with no refresh cookie, /auth/refresh
    // 401s and its catch clears the access token + flips state to signed_out,
    // clobbering the just-exchanged impersonation session and bouncing the rep
    // to /login. Skip the silent refresh here and let the exchange win.
    if (window.location.pathname.startsWith('/impersonate')) return;
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const tokens = await authApi.login({ email, password });
      resetQueryCache();
      setState({ status: 'signed_in', user: tokens.user, impersonation: null });
    },
    [resetQueryCache]
  );

  const logout = useCallback(async () => {
    await authApi.logout();
    resetQueryCache();
    setState({ status: 'signed_out' });
  }, [resetQueryCache]);

  const startImpersonationFromGrant = useCallback(
    async (grant_token: string) => {
      const res = await imp.exchangeGrant(grant_token);
      resetQueryCache();
      setState({
        status: 'signed_in',
        user: res.user,
        impersonation: res.impersonation,
      });
    },
    [resetQueryCache]
  );

  const startImpersonationAsEmployee = useCallback(
    async (target_customer_user_id: string, target_company_id?: string | null) => {
      const res = await imp.startImpersonationAsEmployee(
        target_customer_user_id,
        target_company_id,
      );
      resetQueryCache();
      setState({
        status: 'signed_in',
        user: res.user,
        impersonation: res.impersonation,
      });
    },
    [resetQueryCache]
  );

  const endImpersonationFn = useCallback(async () => {
    await imp.endImpersonation();
    resetQueryCache();
    setState({ status: 'signed_out' });
  }, [resetQueryCache]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      logout,
      refresh,
      startImpersonationFromGrant,
      startImpersonationAsEmployee,
      endImpersonation: endImpersonationFn,
    }),
    [
      state,
      login,
      logout,
      refresh,
      startImpersonationFromGrant,
      startImpersonationAsEmployee,
      endImpersonationFn,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// True when the current session is an active impersonation. Use this
// to hide payment / identity-change surfaces (server enforces the
// block; SPA hides the entry point for clarity).
export function useIsImpersonating(): boolean {
  const auth = useAuth();
  return auth.status === 'signed_in' && auth.impersonation !== null;
}
